/*
 *  Feature Buffer Tool
 *  2026-03-10
 *  Allows user to select a layer (roads, rivers, easements, etc.),
 *  drag a rectangle or click to select features on the map, enter a buffer
 *  distance, and find neighboring parcels within that distance.
 *
 *  Selection: drag a rectangle to select all features within, or click
 *  to select individual features. Repeat to add more. Then Run Buffer.
 *
 *  Configuration in county conf.js:
 *    rwgInitParams.featureBufferLayers = [
 *      { id: 'road_cl', label: 'Roads', table: 'road_cl', idCol: 'ogc_fid', nameCol: 'label' },
 *      ...
 *    ];
 */

import sourceVector from '../node_modules/ol/source/Vector.js';
import layerVector from '../node_modules/ol/layer/Vector.js';
import Feature from '../node_modules/ol/Feature.js';
import Polygon from '../node_modules/ol/geom/Polygon.js';
import MultiPolygon from '../node_modules/ol/geom/MultiPolygon.js';
import Point from '../node_modules/ol/geom/Point.js';
import MultiPoint from '../node_modules/ol/geom/MultiPoint.js';
import LineString from '../node_modules/ol/geom/LineString.js';
import MultiLineString from '../node_modules/ol/geom/MultiLineString.js';
import DragBox from '../node_modules/ol/interaction/DragBox.js';
import {createEmpty, extend} from '../node_modules/ol/extent.js';
import {unByKey} from '../node_modules/ol/Observable.js';

const olGeoms = {
  Polygon, MultiPolygon, Point, MultiPoint, LineString, MultiLineString
};

export default function(map) {

  const featureBufferLayers = rwg.config.featureBufferLayers || [];
  if (!featureBufferLayers.length) return function() {};

  const bufferLayerSource = new sourceVector();
  const featureLayerSource = new sourceVector();
  let clickListenerKey = null;
  let dragBox = null;
  let selectedLayer = null;
  let selectedFeatureIds = [];
  let selectedFeatureNames = [];
  let active = false;
  let floatingPanel = null;

  // layer for neighboring parcels (orange)
  map.addLayer(new layerVector({
    title: 'lyrFeatureBuffer',
    source: bufferLayerSource,
    style: {
      "circle-radius": 5,
      "circle-fill-color": 'rgba(255, 96, 0, 0.5)',
      "circle-stroke-color": 'rgba(255, 96, 0, 0.5)',
      "stroke-color": 'rgba(255, 96, 0, 0.5)',
      "stroke-width": 6
    },
    zIndex: 6
  }));

  // layer for selected features (dark purple, thick)
  map.addLayer(new layerVector({
    title: 'lyrFeatureBufferHighlight',
    source: featureLayerSource,
    style: {
      "stroke-color": 'rgba(100, 0, 160, 0.9)',
      "stroke-width": 7,
      "fill-color": 'rgba(100, 0, 160, 0.15)',
      "circle-radius": 9,
      "circle-fill-color": 'rgba(100, 0, 160, 0.7)'
    },
    zIndex: 7
  }));

  // buffer ring style
  rwg.styles.add('featureBufferStyle', {
    stroke: {
      color: 'rgba(85, 170, 255, 0.8)',
      width: 6
    }
  });

  // ── Floating panel (non-modal, doesn't block map) ──────────────────

  function createFloatingPanel() {
    if (floatingPanel) return;
    floatingPanel = document.createElement('div');
    floatingPanel.id = 'fb-float-panel';
    floatingPanel.style.cssText =
      'position:absolute; top:10px; left:50%; transform:translateX(-50%); z-index:999; ' +
      'background:white; border:2px solid #666; border-radius:8px; padding:10px 18px; ' +
      'box-shadow:0 2px 8px rgba(0,0,0,0.3); font-size:1.1em; text-align:center; ' +
      'pointer-events:auto; max-width:90%;';
    document.getElementById('map-div').appendChild(floatingPanel);
  }

  function removeFloatingPanel() {
    if (floatingPanel && floatingPanel.parentNode) {
      floatingPanel.parentNode.removeChild(floatingPanel);
    }
    floatingPanel = null;
  }

  function updateFloatingPanel() {
    if (!floatingPanel) return;
    const count = selectedFeatureIds.length;

    if (count === 0) {
      floatingPanel.innerHTML =
        `<span style="color:#c33; font-weight:bold;">Drag a rectangle or click to select ${selectedLayer.label} features.</span><br>` +
        `<span style="font-size:0.9em;">Press Escape to cancel.</span>`;
    } else {
      const names = selectedFeatureNames.length > 5
        ? selectedFeatureNames.slice(0, 5).join(', ') + ` ... (+${selectedFeatureNames.length - 5} more)`
        : selectedFeatureNames.join(', ');
      floatingPanel.innerHTML =
        `<b>${count} ${selectedLayer.label} feature${count > 1 ? 's' : ''} selected</b><br>` +
        `<span style="color:#555; font-size:0.9em;">${names}</span><br>` +
        `<span style="font-size:0.9em;">Drag or click to add more, or:</span><br>` +
        `<button id="btn-fb-run" class="rwg-btn" style="margin:6px 8px 0; padding:4px 16px;">Run Buffer</button>` +
        `<button id="btn-fb-done-cancel" class="rwg-btn" style="margin:6px 8px 0; padding:4px 16px;">Cancel</button>`;

      document.getElementById('btn-fb-run').onclick = () => {
        removeFloatingPanel();
        stopMapClick();
        runBuffer();
      };
      document.getElementById('btn-fb-done-cancel').onclick = () => {
        removeFloatingPanel();
        stopMapClick();
        featureLayerSource.clear();
        selectedFeatureIds = [];
        selectedFeatureNames = [];
      };
    }
  }

  // ── Initial dialog (layer + distance) ──────────────────────────────

  function openDialog() {
    const content = document.getElementById('rwg-dialog-content');
    const layerOptions = featureBufferLayers.map(lyr =>
      `<option value="${lyr.id}">${lyr.label}</option>`
    ).join('');

    const defaultDist = rwg.config.defaultBufferDistance || 800;

    content.innerHTML = `
      <div style="width:25em; text-align:center">
        <form style="font-size: 1.3em; text-align: left; padding: 10px;">
          <label>Layer:<br><select id="fb-layer" class="w3-border w3-round"
            style="width: 100%; padding: 4px; margin: 4px 0 12px 0;">
            ${layerOptions}</select></label>
          <label>Buffer Distance (ft):<br><input type="text" id="fb-distance"
            value="${defaultDist}" size="8" class="w3-border w3-round"
            style="padding: 4px; margin: 4px 0 12px 0;" /></label>
          <hr>
          <div style="text-align: center;">
            <button id="btn-fb-start" style="margin-right: 15px;" class="rwg-btn">Select Features on Map</button>
            <button id="btn-fb-cancel" class="rwg-btn">Cancel</button>
          </div>
        </form>
      </div>`;
    document.getElementById('rwg-dialog').style.display = 'block';

    document.getElementById('btn-fb-start').onclick = (evt) => {
      evt.preventDefault();
      const layerId = document.getElementById('fb-layer').value;
      const bufferDist = document.getElementById('fb-distance').value;

      if (!bufferDist || isNaN(bufferDist) || Number(bufferDist) <= 0) {
        window.alert('Please enter a valid buffer distance.');
        return;
      }

      selectedLayer = featureBufferLayers.find(l => l.id === layerId);
      if (!selectedLayer) return;

      selectedLayer._bufferDist = Number(bufferDist);

      // reset multi-select state
      selectedFeatureIds = [];
      selectedFeatureNames = [];
      featureLayerSource.clear();

      // close the modal dialog
      document.getElementById('rwg-dialog-content').innerHTML = '';
      document.getElementById('rwg-dialog').style.display = 'none';

      startMapClick();
    };

    document.getElementById('btn-fb-cancel').onclick = (evt) => {
      evt.preventDefault();
      document.getElementById('rwg-dialog-content').innerHTML = '';
      document.getElementById('rwg-dialog').style.display = 'none';
    };
  }

  // ── Map interaction: click + drag-box ──────────────────────────────

  function startMapClick() {
    createFloatingPanel();
    updateFloatingPanel();
    document.getElementById('map-div').style.cursor = 'crosshair';
    active = true;

    // single click for individual feature selection
    clickListenerKey = map.on('singleclick', onMapClick);

    // drag-box for rectangle selection
    dragBox = new DragBox({
      boxEndCondition: function() { return true; }
    });
    map.addInteraction(dragBox);
    dragBox.on('boxend', onBoxEnd);

    document.addEventListener('keydown', onEscKey);
  }

  function stopMapClick() {
    if (clickListenerKey) {
      unByKey(clickListenerKey);
      clickListenerKey = null;
    }
    if (dragBox) {
      map.removeInteraction(dragBox);
      dragBox = null;
    }
    document.removeEventListener('keydown', onEscKey);
    document.getElementById('map-div').style.cursor = '';
    active = false;
  }

  function onEscKey(e) {
    if (e.key === 'Escape') {
      removeFloatingPanel();
      stopMapClick();
      featureLayerSource.clear();
      selectedFeatureIds = [];
      selectedFeatureNames = [];
    }
  }

  // single click: identify nearest feature
  function onMapClick(evt) {
    const coord = evt.coordinate;

    const payload = {
      action: 'identify',
      layer: selectedLayer.table || selectedLayer.id,
      schema: selectedLayer.schema || 'work',
      idCol: selectedLayer.idCol || 'ogc_fid',
      nameCol: selectedLayer.nameCol || 'label',
      point: coord
    };

    fetch('featureBufferJSON.php', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
      .then(response => response.json())
      .then(resultObj => {
        if (resultObj.error) {
          window.alert(resultObj.error);
          return;
        }
        addFeatureToSelection(resultObj);
      });
  }

  // drag-box: select all features within the rectangle
  function onBoxEnd() {
    const extent = dragBox.getGeometry().getExtent();

    const payload = {
      action: 'identify-box',
      layer: selectedLayer.table || selectedLayer.id,
      schema: selectedLayer.schema || 'work',
      idCol: selectedLayer.idCol || 'ogc_fid',
      nameCol: selectedLayer.nameCol || 'label',
      bbox: extent  // [minX, minY, maxX, maxY]
    };

    fetch('featureBufferJSON.php', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
      .then(response => response.json())
      .then(resultObj => {
        if (resultObj.error) {
          window.alert(resultObj.error);
          return;
        }
        // resultObj.features is an array of {id, name, geom}
        if (resultObj.features && resultObj.features.length) {
          resultObj.features.forEach(f => addFeatureToSelection(f));
        } else {
          window.alert('No features found in that area.');
        }
      });
  }

  function addFeatureToSelection(feat) {
    // avoid duplicates
    if (selectedFeatureIds.includes(feat.id)) return;

    selectedFeatureIds.push(feat.id);
    selectedFeatureNames.push(feat.name || `#${feat.id}`);

    // draw the feature (dark purple highlight)
    if (feat.geom) {
      const featGeom = JSON.parse(feat.geom);
      const feature = new Feature({
        geometry: new olGeoms[featGeom.type](featGeom.coordinates)
      });
      featureLayerSource.addFeature(feature);
    }

    updateFloatingPanel();
  }

  // ── Run buffer ─────────────────────────────────────────────────────

  function runBuffer() {
    createFloatingPanel();
    floatingPanel.innerHTML =
      `<span style="color:#666;">Searching for parcels within ${selectedLayer._bufferDist} ft of ` +
      `${selectedFeatureIds.length} feature${selectedFeatureIds.length > 1 ? 's' : ''}...</span>`;

    const payload = {
      action: 'buffer',
      layer: selectedLayer.table || selectedLayer.id,
      schema: selectedLayer.schema || 'work',
      idCol: selectedLayer.idCol || 'ogc_fid',
      nameCol: selectedLayer.nameCol || 'label',
      featureIds: selectedFeatureIds,
      buffer: selectedLayer._bufferDist
    };

    fetch('featureBufferJSON.php', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
      .then(response => response.json())
      .then(resultObj => {
        removeFloatingPanel();
        if (resultObj.error) {
          window.alert(resultObj.error);
          return;
        }
        displayResults(resultObj);
      });
  }

  // ── Display results ────────────────────────────────────────────────

  function displayResults(resultObj) {
    const infotoolDiv = document.querySelector('#infotool');
    const heading = document.createElement('div');
    const csvLink = document.createElement('a');
    const copyButton = document.createElement('button');

    bufferLayerSource.clear();

    // 1. Draw buffer ring (blue)
    if (resultObj.buffer) {
      const bufferGeom = JSON.parse(resultObj.buffer);
      const bufferFeature = new Feature(
        new olGeoms[bufferGeom.type](bufferGeom.coordinates)
      );
      bufferFeature.setStyle(rwg.styles.get('featureBufferStyle'));
      bufferLayerSource.addFeature(bufferFeature);
    }

    // 2. Create SelectionSet for neighboring parcels (orange)
    rwg.selection.featureBufferAdjoiners = new rwg.SelectionSet([
      [false], // row number
      [''],[''],[''],
      [false, dat => {
        const geom = JSON.parse(dat);
        const feature = new Feature({
          geometry: new olGeoms[geom.type](geom.coordinates)
        });
        bufferLayerSource.addFeature(feature);
      }]
    ]);
    rwg.selection.featureBufferAdjoiners.update(resultObj.records);
    const content = rwg.selection.featureBufferAdjoiners.render(null, resultObj.heading);

    // 3. Clear info panel and populate
    infotoolDiv.innerHTML = '';

    csvLink.innerText = 'Download CSV File';
    csvLink.style.margin = '1em';
    let csvContent = '"Name","Address 1","Address 2"\n';
    const adjoiners = rwg.selection.featureBufferAdjoiners.selection;
    for (let row in adjoiners) {
      csvContent += '"' + adjoiners[row].slice(1, 4).join('","') + '"\n';
    }
    const csvContentBlob = new Blob([csvContent], { 'type': 'text/csv' });
    csvLink.href = URL.createObjectURL(csvContentBlob);
    csvLink.setAttribute('download', 'feature-buffer.csv');
    csvLink.setAttribute('type', 'text/csv');

    heading.appendChild(csvLink);

    copyButton.id = 'copy-button';
    copyButton.innerText = 'Copy to Clipboard';
    heading.appendChild(copyButton);

    infotoolDiv.appendChild(heading);
    infotoolDiv.appendChild(content);
    rwg.tabs.activate('side-info');

    // zoom to combined extent
    const allFeatures = bufferLayerSource.getFeatures().concat(featureLayerSource.getFeatures());
    if (allFeatures.length) {
      const ext = createEmpty();
      allFeatures.forEach(f => extend(ext, f.getGeometry().getExtent()));
      map.getView().fit(ext, {
        padding: [50, 50, 50, 50],
        maxZoom: 8
      });
    }
  }

  // switchboard
  return function(action) {
    if (action === 'featurebuffer') {
      openDialog();
    } else if (action === 'clear') {
      removeFloatingPanel();
      stopMapClick();
      bufferLayerSource.clear();
      featureLayerSource.clear();
      selectedFeatureIds = [];
      selectedFeatureNames = [];
    }
  };
}
